import type { Pagination } from '@frontend/libs/types/common'

import { LOAD_MORE_OFFSET } from '@frontend/libs/constants'
import { endpoint } from '@shared/services/endpoint'
import { getRequest } from '@shared/services/fetcher'
import { useEffect } from 'react'
import useSWRInfinite from 'swr/infinite'

import type { Post } from '../../../libs/types/post'

import BlogItem from '../../../components/blog/BlogItem'
import BlogListLoader from './BlogListLoader'

const PAGE_SIZE = 6

type PostMeta = {
  attributes?: Record<string, unknown>[]
  pagination?: Pagination
}

function BlogList() {
  const { data, isLoading, isValidating, mutate, setSize, size } =
    useSWRInfinite(
      (index) => `${endpoint.frontend.POSTS}?page=${index + 1}`,
      getRequest,
    )

  const posts: Post[] = data?.[0]?.success
    ? [].concat(...data.map((item) => item.data))
    : []
  const meta: PostMeta = data?.[0]?.success ? data[0].meta : {}

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')
  const isEmpty = data?.[0]?.length === 0
  const isReachingEnd =
    isEmpty || (data && posts.length >= meta.pagination.total)
  const isRefreshing = isValidating && data && data.length === size

  useEffect(() => {
    const handleScroll = async () => {
      const windowScroll =
        window.innerHeight + document.documentElement.scrollTop
      const documentHeight =
        document.documentElement.offsetHeight - LOAD_MORE_OFFSET
      const isLoadMorePosition = windowScroll >= documentHeight

      if (isLoadMorePosition && !isLoadingMore && !isReachingEnd) {
        setSize(size + 1)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoadingMore, isReachingEnd, setSize, size])

  const renderPosts = () =>
    posts?.map((post) => (
      <BlogItem
        categories={post.categories}
        excerpt={post.excerpt}
        image={post.image}
        key={post.id}
        permalink={post.permalink}
        title={post.title}
      />
    ))

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {posts && renderPosts()}
      {isLoadingMore && <BlogListLoader />}
    </div>
  )
}

export default BlogList
